// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Icon,
  Form,
  Message,
  Segment,
  Divider,
  TextArea,
  Dropdown,
  Breadcrumb
} from 'semantic-ui-react';
import { brokerageApi } from '../api/brokerageApi';
import { brokerageProviderApi } from '../api/brokerageProviderApi';
import { providerApi, Provider } from '../api/providerApi';
import { templateApi, LOATemplate } from '../api/templateApi';
import { providerEmailOverridesApi, ProviderEmailOverride } from '../api/providerEmailOverridesApi';

function BrokerageProviderEditPage() {
  const navigate = useNavigate();
  const { brokerageId, providerId } = useParams<{ brokerageId: string; providerId: string }>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [provider, setProvider] = useState<Provider | null>(null);
  const [templates, setTemplates] = useState<LOATemplate[]>([]);
  const [emailOverride, setEmailOverride] = useState<ProviderEmailOverride | null>(null);
  const [brokerageName, setBrokerageName] = useState<string>('');

  // Form state
  const [isEnabled, setIsEnabled] = useState(true);
  const [customTemplateId, setCustomTemplateId] = useState<string>('');
  const [overrideEmail, setOverrideEmail] = useState('');
  const [emailOverrideReason, setEmailOverrideReason] = useState('');
  const [emailSubjectTemplate, setEmailSubjectTemplate] = useState('');
  const [emailBodyTemplateHtml, setEmailBodyTemplateHtml] = useState('');
  const [emailBodyTemplateText, setEmailBodyTemplateText] = useState('');
  const [agencyCode, setAgencyCode] = useState('');

  const loadData = useCallback(async () => {
    if (!brokerageId || !providerId) return;

    setLoading(true);
    setError(null);
    try {
      // Load brokerage name for breadcrumbs
      const brokerageData = await brokerageApi.getById(brokerageId);
      setBrokerageName(brokerageData.name);

      // Load provider details
      const providerData = await providerApi.getById(providerId);
      setProvider(providerData);

      // Load templates
      const templatesData = await templateApi.getAll();
      setTemplates(templatesData.content);

      // Load provider configuration (may not exist yet)
      try {
        const configData = await brokerageProviderApi.getById(brokerageId, providerId);
        setIsEnabled(configData.isEnabled || false);
        setCustomTemplateId(configData.customTemplateId || '');
        setEmailSubjectTemplate(configData.emailSubjectTemplate || '');
        setEmailBodyTemplateHtml(configData.emailBodyTemplateHtml || '');
        setEmailBodyTemplateText(configData.emailBodyTemplateText || '');
        setAgencyCode(configData.agencyCode || '');
      } catch (error) {
        // Config doesn't exist yet - that's OK, we'll create it on save
        console.error('Error:', error);
      }

      // Load email override for this provider (provider-level only, not policy-type level)
      const emailOverridesData = await providerEmailOverridesApi.getProviderLevelOverrides(brokerageId);
      const override = emailOverridesData.find(
        (o) => o.providerId === providerId && o.policyTypeId === null
      );
      if (override) {
        setEmailOverride(override);
        setOverrideEmail(override.overrideEmail);
        setEmailOverrideReason(override.emailOverrideReason || '');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brokerageId, providerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!brokerageId || !providerId) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Update or create provider configuration
      const configPayload = {
        brokerageId,
        providerId,
        isEnabled,
        customTemplateId: customTemplateId || undefined,
        emailSubjectTemplate: emailSubjectTemplate || undefined,
        emailBodyTemplateHtml: emailBodyTemplateHtml || undefined,
        emailBodyTemplateText: emailBodyTemplateText || undefined,
        agencyCode: agencyCode || undefined
      };

      await brokerageProviderApi.update(brokerageId, providerId, configPayload);

      // Update or create email override if email is provided
      if (overrideEmail) {
        const overridePayload = {
          brokerageId,
          providerId,
          policyTypeId: null, // Provider-level override
          overrideEmail,
          emailOverrideReason
        };

        if (emailOverride) {
          await providerEmailOverridesApi.update(emailOverride.id!, overridePayload);
        } else {
          await providerEmailOverridesApi.create(overridePayload);
        }
      } else if (emailOverride) {
        // Email override was removed
        await providerEmailOverridesApi.delete(emailOverride.id!);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/brokerages/${brokerageId}/providers`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const templateOptions = [
    { key: 'default', value: '', text: 'Use Default Template (from Brokerage)' },
    ...templates.map((t) => ({
      key: t.id,
      value: t.id,
      text: t.name
    }))
  ];

  if (loading) {
    return (
      <Container style={{ padding: '2rem' }}>
        <Message icon>
          <Icon name='circle notched' loading />
          <Message.Content>
            <Message.Header>Loading Configuration</Message.Header>
            Please wait while we load the provider configuration...
          </Message.Content>
        </Message>
      </Container>
    );
  }

  return (
    <Container style={{ padding: '2rem' }}>
      <Breadcrumb>
        <Breadcrumb.Section link onClick={() => navigate('/brokerages')}>
          Brokerages
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section link onClick={() => navigate(`/brokerages/${brokerageId}/providers`)}>
          {brokerageName || 'Brokerage'}
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section active>
          {provider?.name || 'Provider'}
        </Breadcrumb.Section>
      </Breadcrumb>

      <Header as='h1' style={{ marginTop: '1rem' }}>
        <Icon name='building' />
        <Header.Content>
          Configure Provider: {provider?.name || 'Unknown'}
          <Header.Subheader>
            Provider Override (Base) — applies to all policy types unless overridden
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Configuration Saved</Message.Header>
          <p>Provider configuration updated successfully. Redirecting...</p>
        </Message>
      )}

      <Segment>
        <Header as='h3'>
          <Icon name='settings' />
          General Settings
        </Header>

        <Form>
          <Form.Checkbox
            label='Enable this provider for the brokerage'
            checked={isEnabled}
            onChange={(_, data) => setIsEnabled(data.checked || false)}
          />

          <Form.Input
            label='Agency Code'
            placeholder='e.g. STL 12364'
            value={agencyCode}
            onChange={(e) => setAgencyCode(e.target.value)}
            maxLength={50}
          />
          <p style={{ fontSize: '0.85em', color: '#666', marginTop: '-0.5em' }}>
            The brokerage-level agency code for this provider. Used as default when advisors do not have their own code.
          </p>
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>
          <Icon name='file' />
          LOA Template Override
        </Header>
        <Message info>
          <Message.Header>Provider Override (Base)</Message.Header>
          <p>
            This configuration applies to all policy types for this provider, unless overridden
            by a policy-type-specific configuration (Policy Type Override).
          </p>
        </Message>

        <Form>
          <Form.Field>
            <label htmlFor="loa-template-dropdown">LOA Template</label>
            <Dropdown
              id="loa-template-dropdown"
              placeholder='Select template'
              fluid
              selection
              options={templateOptions}
              value={customTemplateId}
              onChange={(_, data) => setCustomTemplateId(data.value as string)}
            />
          </Form.Field>
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>
          <Icon name='mail' />
          Email Override
        </Header>
        <Message warning>
          <Message.Header>Provider-Level Email Override</Message.Header>
          <p>
            Override the provider's default email address for ALL policy types from this provider.
            Policy-type-specific overrides (Policy Type Override) will take precedence.
          </p>
        </Message>

        <Form>
          <Form.Field>
            <label htmlFor="override-email-input">Override Email Address</label>
            <Form.Input
              id="override-email-input"
              type='email'
              placeholder='provider-override@example.com'
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
            />
            <small>Leave empty to use provider's default email</small>
          </Form.Field>

          <Form.Field>
            <label htmlFor="override-reason-textarea">Reason for Override (Optional)</label>
            <Form.TextArea
              id="override-reason-textarea"
              placeholder='e.g., Dedicated representative for this brokerage'
              value={emailOverrideReason}
              onChange={(e) => setEmailOverrideReason(e.target.value)}
              rows={2}
            />
          </Form.Field>
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>
          <Icon name='envelope' />
          Email Notification Templates
        </Header>
        <Message info>
          <Message.Header>Email Template Configuration</Message.Header>
          <p>
            Customize email notifications for LOA events involving this provider. Leave empty to use
            brokerage defaults. Use <code>{'{{variable}}'}</code> for subject and{' '}
            <code>{'[[${variable}]]'}</code> for HTML body.
          </p>
        </Message>

        <Form>
          <Form.Field>
            <label htmlFor="email-subject-template-input">Email Subject Template</label>
            <Form.Input
              id="email-subject-template-input"
              placeholder='LOA Update: {{policyType}} from {{providerName}}'
              value={emailSubjectTemplate}
              onChange={(e) => setEmailSubjectTemplate(e.target.value)}
            />
            <small>
              Available variables: {'{{'}advisorName{'}}'}
              , {'{{'}clientName{'}}'}
              , {'{{'}policyType{'}}'}
              , {'{{'}providerName{'}}'}
            </small>
          </Form.Field>

          <Form.Field>
            <label htmlFor="email-body-html-textarea">Email Body Template (HTML)</label>
            <TextArea
              id="email-body-html-textarea"
              placeholder='<h1>Dear [[${advisorName}]],</h1><p>LOA update for [[${policyType}]]</p>'
              value={emailBodyTemplateHtml}
              onChange={(e) => setEmailBodyTemplateHtml(e.target.value)}
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
            <small>
              Thymeleaf syntax: <code>[[${'{'}variable{'}'}]]</code> or{' '}
              <code>th:text="${'{'}variable{'}'}"</code>
            </small>
          </Form.Field>

          <Form.Field>
            <label htmlFor="email-body-text-textarea">Email Body Template (Plain Text - Fallback)</label>
            <TextArea
              id="email-body-text-textarea"
              placeholder='Dear {{advisorName}}, LOA update for {{policyType}}'
              value={emailBodyTemplateText}
              onChange={(e) => setEmailBodyTemplateText(e.target.value)}
              rows={6}
              style={{ fontFamily: 'monospace' }}
            />
            <small>
              Plain text fallback for non-HTML email clients. Use {'{{'}variable{'}}'}
            </small>
          </Form.Field>
        </Form>
      </Segment>

      <Divider />

      <Button.Group>
        <Button onClick={() => navigate(`/brokerages/${brokerageId}/providers`)}>Cancel</Button>
        <Button.Or />
        <Button primary onClick={handleSave} loading={saving} disabled={saving}>
          <Icon name='save' />
          Save Configuration
        </Button>
      </Button.Group>
    </Container>
  );
}

export default BrokerageProviderEditPage;
